"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import Link from "next/link";
import {
  Monitor, Smartphone, Tablet, Tv, Chrome, Globe, Download,
} from "lucide-react";

const PLATFORM_ICON_MAP: Record<string, React.ComponentType<any>> = {
  Monitor, Smartphone, Tablet, Tv, Chrome, Globe, Download,
  monitor: Monitor, smartphone: Smartphone, tablet: Tablet,
  tv: Tv, chrome: Chrome, globe: Globe, download: Download,
  windows: Monitor, mac: Monitor, macos: Monitor, linux: Monitor,
  android: Smartphone, ios: Smartphone, iphone: Smartphone,
  ipad: Tablet, "apple-tv": Tv, "fire-tv": Tv, "android-tv": Tv,
  browser: Chrome, extension: Chrome,
};

interface Platform {
  name: string;
  icon?: string;
  href: string;
}

interface PlatformDownloadsProps {
  title?: string;
  subtitle?: string;
  platforms: Platform[];
}

const PlatformDownloads = ({ title, subtitle, platforms }: PlatformDownloadsProps) => {
  if (!platforms || platforms.length === 0) return null;

  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-5xl mx-auto">
        {/* Header */}
        {(title || subtitle) && (
          <div className="text-center mb-12 space-y-4">
            {title && (
              <BlurFade delay={0.1}>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                  {title}
                </h2>
              </BlurFade>
            )}
            {subtitle && (
              <BlurFade delay={0.15}>
                <p className="text-foreground/60 text-lg max-w-2xl mx-auto">
                  {subtitle}
                </p>
              </BlurFade>
            )}
          </div>
        )}

        {/* Platform Buttons */}
        <BlurFade delay={0.2}>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {platforms.map((platform, i) => {
              const Icon = platform.icon
                ? (PLATFORM_ICON_MAP[platform.icon] || Download)
                : Download;
              return (
                <Link
                  key={i}
                  href={platform.href}
                  className="flex items-center gap-3 px-6 py-4 bg-muted/50 hover:bg-muted border border-foreground/5 rounded-2xl transition-all hover:shadow-md group"
                >
                  <Icon size={22} className="text-foreground/50 group-hover:text-foreground/70 transition-colors" strokeWidth={1.5} />
                  <span className="text-sm font-bold text-foreground/70 group-hover:text-foreground transition-colors">
                    {platform.name}
                  </span>
                </Link>
              );
            })}
          </div>
        </BlurFade>
      </div>
    </section>
  );
};

export default PlatformDownloads;
